import {
  Award,
  Users,
  Target,
  GraduationCap,
  Quote,
  MapPin,
} from "lucide-react";

const stats = [
  {
    id: "legacy",
    icon: Award,
    value: "26+",
    label: "Years Legacy",
    sub: "Established 2000",
  },
  {
    id: "students",
    icon: Users,
    value: "15,000+",
    label: "Students Trained",
    sub: "Across SSC & HSC",
  },
  {
    id: "result",
    icon: Target,
    value: "100%",
    label: "Result Focused",
    sub: "Year after year",
  },
  {
    id: "streams",
    icon: GraduationCap,
    value: "5–12",
    label: "Std Coverage",
    sub: "School + JEE/NEET/CET",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-14 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-red-700">
            About Gurukul
          </div>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">
            A quiet 26-year legacy of{" "}
            <span className="text-red-600">discipline &amp; results.</span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-600 md:text-base">
            Since the year 2000, Gurukul Classes has stood for one thing in
            Vitawa, Thane — focused, student-first coaching that prefers
            consistency over noise. Small batches. Detailed notes. Honest
            feedback. Results that speak for themselves.
          </p>
        </div>

        {/* Stat grid — 2 cols on mobile, 4 on desktop */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-3 md:mt-14 md:grid-cols-4 md:gap-5">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                data-testid={`about-stat-${s.id}`}
                style={{ animationDelay: `${i * 60}ms` }}
                className="fade-up group relative overflow-hidden rounded-md border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-red-300 hover:shadow-lg md:p-5"
              >
                <span className="pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full bg-red-100/70 transition-transform group-hover:scale-125" />
                <div className="relative flex h-9 w-9 items-center justify-center rounded-md bg-red-600 text-white shadow-sm md:h-10 md:w-10">
                  <Icon className="h-4 w-4 md:h-5 md:w-5" strokeWidth={2.2} />
                </div>
                <div className="relative mt-3">
                  <div className="font-display text-2xl font-extrabold leading-none text-slate-900 md:text-3xl">
                    {s.value}
                  </div>
                  <div className="mt-1.5 text-[12px] font-bold uppercase tracking-wide text-red-700 md:text-[13px]">
                    {s.label}
                  </div>
                  <div className="mt-0.5 text-[11px] leading-snug text-slate-500 md:text-[12px]">
                    {s.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Hook-worthy description box */}
        <div
          data-testid="about-description"
          className="mx-auto mt-12 max-w-3xl md:mt-16"
        >
          <div className="relative overflow-hidden rounded-md border border-red-100 bg-gradient-to-br from-red-50 via-white to-orange-50 px-5 pb-6 pt-9 shadow-sm md:px-10 md:pb-10 md:pt-12">
            {/* Floating quote badge */}
            <span className="absolute -top-5 left-5 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-600/30 md:left-10 md:h-12 md:w-12">
              <Quote className="h-5 w-5 md:h-6 md:w-6" strokeWidth={2.2} />
            </span>

            {/* Eyebrow */}
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-red-700 md:text-[11px]">
              Who We Are
            </div>

            {/* Hook headline */}
            <h3 className="mt-2 font-display text-xl font-extrabold leading-snug text-slate-900 md:text-2xl">
              A coaching class your neighbour will quietly tell you to trust.
            </h3>

            {/* Accent line */}
            <div className="mt-4 h-0.5 w-12 rounded-full bg-red-600 md:w-16" />

            {/* Body */}
            <p className="mt-5 text-[14px] leading-[1.85] text-slate-700 md:text-[15px]">
              Gurukul Classes is based in{" "}
              <strong className="text-slate-900">Vitawa</strong>, right beside{" "}
              <strong className="text-slate-900">Kunkai Devi Mandir</strong> at{" "}
              <strong className="text-slate-900">
                Priyanka Paradise, Vitawa, Thane
              </strong>
              {" "}— a calm, easy-to-reach pocket of the city that families have
              trusted with their children since the year 2000. Over twenty-six
              years, the institute has grown quietly but steadily, becoming a
              familiar second home for thousands of students from Std 5 right
              through Std 12 (Science &amp; Commerce). What sets Gurukul apart
              is not size or marketing, but{" "}
              <strong className="text-red-700">discipline</strong> — small
              attentive batches, detailed printed notes, regular tests, and
              one-to-one mentoring that catches doubts before they grow. From
              SSC and HSC boards to JEE, NEET and MHT-CET preparation, every
              batch is taught with the same patient, structured,
              results-first approach. If you live in or around Vitawa and want
              a coaching class that simply does the work, Gurukul is built for
              exactly that.
            </p>

            {/* Location chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "Vitawa",
                "Kunkai Devi Mandir",
                "Priyanka Paradise",
                "Thane",
              ].map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white/80 px-3 py-1 text-[11px] font-semibold text-red-700 backdrop-blur-sm md:text-[12px]"
                >
                  <MapPin className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
